import { createElement, useCallback, useMemo } from 'react'
import { ChevronRight } from 'lucide-react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import type { PageDocument } from '@/lib/db/rxdb/hooks/entities/pages'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import TaskSidebar from '@/components/app/sidebars/task'
import {
  useChildProjects,
  usePages,
  useRootProjects,
} from '@/lib/db/rxdb/hooks'
import useLoginStore from '@/store/LoginStore'
import { Loading } from '@/components/app/indicators/Loading'
import { ProjectIcon } from '@/lib/utils/projectIcon'
import { BreadcrumbSubPagesDropdown } from '@/routes/features/pages/components/BreadcrumbSubPagesDropdown'
import { CollapsibleAncestorBreadcrumbs } from '@/routes/features/pages/components/CollapsibleAncestorBreadcrumbs'
import { PageDetailsView } from '@/routes/features/pages/components/PageDetailsView'

export const Route = createFileRoute(
  '/_AppWrapper/_AppLayout/$workspaceSlug/spaces/$spaceKey/projects/$projectKey/pages/$pageSlug',
)({
  component: SpaceProjectPageDetailsPage,
})

export default function SpaceProjectPageDetailsPage() {
  const { workspaceSlug, spaceKey, projectKey, pageSlug } = Route.useParams()
  const navigate = useNavigate()
  const workspaceId = useLoginStore((state) => state.workspaceId)
  const userId = useLoginStore((state) => state.userId)

  const { projects: rootProjects } = useRootProjects()
  const { pages, isLoading: pagesLoading } = usePages()

  const space = useMemo<ProjectDocument | null>(() => {
    return rootProjects.find((p) => {
      if (p.isDeleted) return false
      if (p.projectKey.toLowerCase() !== spaceKey.toLowerCase()) return false
      if (workspaceId && p.envId !== workspaceId) return false
      return true
    }) ?? null
  }, [rootProjects, spaceKey, workspaceId])

  const { projects: childProjects } = useChildProjects(space?.id)

  const project = useMemo<ProjectDocument | null>(() => {
    return childProjects?.find((p) => {
      if (p.isDeleted) return false
      if (p.projectKey.toLowerCase() !== projectKey.toLowerCase()) return false
      return true
    }) ?? null
  }, [childProjects, projectKey])

  const page = useMemo<PageDocument | null>(() => {
    if (!project) return null
    return pages.find((p) => {
      if (p.isDeleted) return false
      if (p.slug !== pageSlug) return false
      if (p.projectId !== project.id) return false
      return true
    }) ?? null
  }, [pages, pageSlug, project])

  const ancestors = useMemo(() => {
    if (!page) return []
    const chain: Array<PageDocument> = []
    let current = page
    while (current.parentPageId) {
      const parent = pages.find((p) => p.id === current.parentPageId && !p.isDeleted)
      if (!parent) break
      chain.unshift(parent)
      current = parent
    }
    return chain
  }, [page, pages])

  const breadcrumb = useMemo(() => {
    if (!space || !project || !page) return null
    return (
      <>
        <Link
          to="/$workspaceSlug/spaces/$spaceKey"
          params={{ workspaceSlug, spaceKey }}
          className="flex items-center gap-1 hover:text-foreground transition-colors truncate"
        >
          <ProjectIcon iconType={space.iconType} icon={space.icon} iconColor={space.iconColor} size={14} />
          <span className="truncate">{space.name}</span>
        </Link>
        <ChevronRight className="size-2.5 flex-shrink-0" />
        <Link
          to="/$workspaceSlug/spaces/$spaceKey/projects/$projectKey"
          params={{ workspaceSlug, spaceKey, projectKey }}
          className="flex items-center gap-1 hover:text-foreground transition-colors truncate"
        >
          <ProjectIcon iconType={project.iconType} icon={project.icon} iconColor={project.iconColor} size={14} />
          <span className="truncate">{project.name}</span>
        </Link>
        <CollapsibleAncestorBreadcrumbs
          ancestors={ancestors}
          projectId={project.id}
          pages={pages}
          renderLink={(ancestor) => (
            <Link
              to="/$workspaceSlug/spaces/$spaceKey/projects/$projectKey/pages/$pageSlug"
              params={{ workspaceSlug, spaceKey, projectKey, pageSlug: ancestor.slug }}
              className="hover:text-foreground transition-colors truncate"
            >
              {ancestor.title || 'Untitled'}
            </Link>
          )}
          onNavigate={(slug) => navigate({
            to: '/$workspaceSlug/spaces/$spaceKey/projects/$projectKey/pages/$pageSlug',
            params: { workspaceSlug, spaceKey, projectKey, pageSlug: slug },
          })}
        />
        <ChevronRight className="size-2.5 flex-shrink-0" />
        <span className="flex items-center gap-0.5 truncate">
          <span className="truncate">{page.title || 'Untitled'}</span>
          <BreadcrumbSubPagesDropdown
            currentPageId={page.id}
            projectId={project.id}
            pages={pages}
            onNavigate={(slug) => navigate({
              to: '/$workspaceSlug/spaces/$spaceKey/projects/$projectKey/pages/$pageSlug',
              params: { workspaceSlug, spaceKey, projectKey, pageSlug: slug },
            })}
          />
        </span>
      </>
    )
  }, [space, project, page, ancestors, workspaceSlug, spaceKey, projectKey, pages, navigate])

  const sidebar = useMemo(() => createElement(TaskSidebar), [])

  const handleNavigateToSubPage = useCallback((slug: string) => {
    navigate({
      to: '/$workspaceSlug/spaces/$spaceKey/projects/$projectKey/pages/$pageSlug',
      params: { workspaceSlug, spaceKey, projectKey, pageSlug: slug },
    })
  }, [navigate, workspaceSlug, spaceKey, projectKey])

  const handleNavigateAfterDelete = useCallback((parentPage: PageDocument | null) => {
    if (parentPage) {
      navigate({
        to: '/$workspaceSlug/spaces/$spaceKey/projects/$projectKey/pages/$pageSlug',
        params: { workspaceSlug, spaceKey, projectKey, pageSlug: parentPage.slug },
      })
      return
    }
    navigate({
      to: '/$workspaceSlug/spaces/$spaceKey/projects/$projectKey',
      params: { workspaceSlug, spaceKey, projectKey },
    })
  }, [navigate, workspaceSlug, spaceKey, projectKey])

  if (pagesLoading) {
    return (
      <div className="flex min-h-full flex-1 items-center justify-center">
        <Loading />
      </div>
    )
  }

  if (!space || !project || !page || !workspaceId) {
    return (
      <div className="flex min-h-full flex-1 items-center justify-center px-4">
        <div className="text-center text-muted-foreground">
          Page not found or may have been removed.
        </div>
      </div>
    )
  }

  return (
    <PageDetailsView
      page={page}
      project={project}
      pages={pages}
      workspaceId={workspaceId}
      userId={userId}
      pageSlug={pageSlug}
      sidebar={sidebar}
      breadcrumb={breadcrumb}
      layoutDeps={[space.id, space.name, space.icon, space.iconType, space.iconColor, project.id, project.name, project.icon, project.iconType, project.iconColor, workspaceSlug, spaceKey, projectKey, pageSlug, page.id, page.title, page.parentPageId, ancestors.length]}
      onNavigateToSubPage={handleNavigateToSubPage}
      onNavigateAfterDelete={handleNavigateAfterDelete}
    />
  )
}
